/**
 * Hand-positioned SVG architecture diagram for the six contracts page.
 *
 * Background: mermaid's auto-layout engines (dagre and ELK) both fail to
 * honor the user's intended 4-tier hierarchy because the protocol has real
 * cycles — value loops between L3 contracts and L4 actors (e.g.
 * Contributors -> MC -> MT -> Contributors). Both engines resolve cycles
 * by reversing edges, and the resulting rank order pushes nodes off-tier.
 *
 * This component renders the canonical layout exactly: governance at top,
 * registry below, four on-chain contracts in a row, three off-chain actors
 * at the bottom aligned with their primary contract. Colors come from CSS
 * variables so light/dark theme switching is automatic.
 */
export default function ContractsArchitecture() {
  return (
    <div className="contracts-architecture-wrap" data-pagefind-ignore>
      <svg
        viewBox="0 0 900 640"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Metha contract architecture in five tiers: OpenGovernance at top; EternalStorage and the planned Multisig council at L2; four on-chain contracts (MethaToken, MethaCrowdsale, AuditingPool, AuditorRegistryV2) at L3; four off-chain participants (ops/dev, Contributors, Labs, Auditor firms) at L4; and the planned Phase 3 settlement layer at L5 (Market / Pharma below Contributors, IP below Labs) closing the value loop."
        className="contracts-architecture-svg"
      >
        <defs>
          <marker id="ca-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" className="ca-arrow-head" />
          </marker>
        </defs>

        {/* ── L1: OpenGovernance — centered vertically above MethaCrowdsale */}
        <g className="ca-node">
          <rect x="260" y="20" width="180" height="60" rx="8" />
          <text x="350" y="46" textAnchor="middle" className="ca-title">OpenGovernance</text>
          <text x="350" y="65" textAnchor="middle" className="ca-subtitle">proposals · vote calculus</text>
        </g>

        {/* ── L2 left: EternalStorage — below OG, aligned with MC ─── */}
        <g className="ca-node">
          <rect x="260" y="130" width="180" height="60" rx="8" />
          <text x="350" y="156" textAnchor="middle" className="ca-title">EternalStorage</text>
          <text x="350" y="175" textAnchor="middle" className="ca-subtitle">slot registry · params</text>
        </g>

        {/* ── L2 right: Multisig council — appointed by OG, acts on AP+AR,
             can flip a killswitch slot in ES. Aligned with the AP+AR span.
             PLANNED (mainnet design); on Sepolia v3 the deployer still holds
             these rights — the dashed stroke + "PLANNED" badge signal this. */}
        <g className="ca-node ca-node-planned">
          <rect x="540" y="130" width="180" height="60" rx="8" strokeDasharray="6 4" />
          <text x="630" y="156" textAnchor="middle" className="ca-title">Multisig council</text>
          <text x="630" y="175" textAnchor="middle" className="ca-subtitle">slash · pause · freeze</text>
          {/* Small "PLANNED" badge so the reader catches the status at a
              glance without needing the prose explanation below the diagram. */}
          <rect x="688" y="120" width="46" height="14" rx="3" className="ca-badge-bg" />
          <text x="711" y="130" textAnchor="middle" className="ca-badge-text">PLANNED</text>
        </g>

        {/* ── L3: four on-chain contracts ──────────────────── */}
        <g className="ca-node">
          <rect x="60"  y="240" width="180" height="100" rx="8" />
          <text x="150" y="270" textAnchor="middle" className="ca-title">MethaToken</text>
          <text x="150" y="293" textAnchor="middle" className="ca-subtitle">ERC20Votes</text>
          <text x="150" y="313" textAnchor="middle" className="ca-subtitle">snapshots</text>

          <rect x="260" y="240" width="180" height="100" rx="8" />
          <text x="350" y="270" textAnchor="middle" className="ca-title">MethaCrowdsale</text>
          <text x="350" y="293" textAnchor="middle" className="ca-subtitle">auction · ETH vault</text>
          <text x="350" y="313" textAnchor="middle" className="ca-subtitle">mints METH</text>

          <rect x="460" y="240" width="180" height="100" rx="8" />
          <text x="550" y="270" textAnchor="middle" className="ca-title">AuditingPool</text>
          <text x="550" y="293" textAnchor="middle" className="ca-subtitle">requests · milestones</text>
          <text x="550" y="313" textAnchor="middle" className="ca-subtitle">DSMB · option exercise</text>

          <rect x="660" y="240" width="180" height="100" rx="8" />
          <text x="750" y="270" textAnchor="middle" className="ca-title">AuditorRegistryV2</text>
          <text x="750" y="293" textAnchor="middle" className="ca-subtitle">firms · stake</text>
          <text x="750" y="313" textAnchor="middle" className="ca-subtitle">attestations</text>
        </g>

        {/* ── L4 — four off-chain actors:
             OPDEV    sits squarely under MT-left          (x=20-160).
             CONTRIB  sits between MT (60-240)  and MC (260-440), centered 250.
             LABS     sits between MC (260-440) and AP (460-640), centered 450.
             AUD      sits between AP (460-640) and AR (660-840), centered 650. */}
        <g className="ca-node ca-node-actor">
          <rect x="20" y="430" width="140" height="50" rx="25" />
          <text x="90" y="461" textAnchor="middle" className="ca-actor">ops / dev</text>

          <rect x="160" y="430" width="180" height="50" rx="25" />
          <text x="250" y="461" textAnchor="middle" className="ca-actor">Contributors</text>

          <rect x="360" y="430" width="180" height="50" rx="25" />
          <text x="450" y="461" textAnchor="middle" className="ca-actor">Labs</text>

          <rect x="560" y="430" width="180" height="50" rx="25" />
          <text x="650" y="461" textAnchor="middle" className="ca-actor">Auditor firms</text>
        </g>

        {/* ── L5: Phase 3 IP-settlement loop (PLANNED, post-mainnet) ──
             Market / Pharma sits below Contributors and trades METH ⇄ ETH/fiat.
             IP sits below Labs and represents the research output that
             Market / Pharma buys with METH. Closes the value loop:
             Contributors fund research → labs produce IP → market buys IP
             with METH → market buys METH from contributors with ETH/fiat. */}
        <g className="ca-node ca-node-actor ca-node-planned">
          <rect x="160" y="560" width="180" height="50" rx="25" strokeDasharray="6 4" />
          <text x="250" y="591" textAnchor="middle" className="ca-actor">Market / Pharma</text>

          <rect x="360" y="560" width="180" height="50" rx="25" strokeDasharray="6 4" />
          <text x="450" y="591" textAnchor="middle" className="ca-actor">IP</text>

          {/* "PHASE 3" badge above IP to signal the whole L5 row is planned. */}
          <rect x="485" y="548" width="52" height="14" rx="3" className="ca-badge-bg" />
          <text x="511" y="558" textAnchor="middle" className="ca-badge-text">PHASE 3</text>
        </g>

        {/* ── Edges ─────────────────────────────────────────── */}
        <g className="ca-edges">
          {/* OG → ES (vertical, since both centered at x=350) */}
          <line x1="350" y1="80" x2="350" y2="128" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="358" y="110" className="ca-label">rebinds slots</text>

          {/* OG → Multisig (elects council). Z-shaped path: down from OG's
               bottom-right corner, right across the OG-L2 gap at y=100,
               then down into Multisig's top edge. Ends at x=590 which is
               inside Multisig (540-720). */}
          <path
            d="M 440 80 L 440 100 L 590 100 L 590 128"
            className="ca-edge"
            markerEnd="url(#ca-arrow)"
            fill="none"
          />
          <text x="515" y="95" className="ca-label">elects</text>

          {/* Multisig → ES (killswitch — emergency-only, dotted) */}
          <line x1="538" y1="160" x2="442" y2="160" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="490" y="152" className="ca-label" textAnchor="middle">killswitch</text>

          {/* ── ES → L3 slot bindings (dotted) ─────────────────── */}
          <line x1="300" y1="190" x2="190" y2="240" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <line x1="350" y1="190" x2="350" y2="240" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <line x1="400" y1="190" x2="500" y2="240" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="350" y="225" className="ca-label" textAnchor="middle">slot bindings</text>

          {/* ── Multisig → AP / AR (vertical, dotted — control authority) ── */}
          <line x1="570" y1="190" x2="570" y2="240" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="565" y="220" className="ca-label" textAnchor="end">slash · pause</text>
          <line x1="690" y1="190" x2="690" y2="240" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="695" y="220" className="ca-label">freeze / validate</text>

          {/* ── L3 internal (all at y=290, arrows in the 20px gaps between
               adjacent contracts) ──────────────────────────────────── */}
          {/* MC → MT (mint) */}
          <line x1="258" y1="290" x2="242" y2="290" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="250" y="282" className="ca-label" textAnchor="middle">mint</text>

          {/* AP → MC (approves ETH funds out) */}
          <line x1="458" y1="290" x2="442" y2="290" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="450" y="282" className="ca-label" textAnchor="middle">approves ETH</text>

          {/* AP — AR (constructor wire, no arrow) */}
          <line x1="640" y1="290" x2="660" y2="290" className="ca-edge" />
          <text x="650" y="282" className="ca-label" textAnchor="middle">Auth</text>

          {/* ── L3 ↔ L4 — vertical arrows. Labels staggered Y to avoid
               collisions: DOWN-arrow labels at y=370 (left side), UP-arrow
               labels at y=410 (right side). ─────────────────────────── */}

          {/* Operations / dev:  MT → OPDEV (METH 30%) */}
          <line x1="100" y1="340" x2="100" y2="430" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="92" y="370" className="ca-label" textAnchor="end">METH 30%</text>

          {/* Contributors:  MT → CONTRIB (METH 70%)  |  CONTRIB → MC (ETH) */}
          <line x1="200" y1="340" x2="200" y2="430" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="192" y="370" className="ca-label" textAnchor="end">METH 70%</text>
          <line x1="300" y1="430" x2="300" y2="340" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="308" y="410" className="ca-label">ETH contribution</text>

          {/* Labs:  MC → LABS (ETH milestone)  |  LABS → AP (request/proof) */}
          <line x1="400" y1="340" x2="400" y2="430" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="392" y="370" className="ca-label" textAnchor="end">ETH milestone</text>
          <line x1="500" y1="430" x2="500" y2="340" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="508" y="410" className="ca-label">request/proof</text>

          {/* Auditors:  AP → AUD (rewards)  |  AUD → AP (decisions)  |  AUD → AR (stake).
               stake arrow shifted to x=720 to give "cast decision" room. */}
          <line x1="580" y1="340" x2="580" y2="430" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="572" y="370" className="ca-label" textAnchor="end">METH reward</text>
          <line x1="620" y1="430" x2="620" y2="340" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="628" y="410" className="ca-label">cast decision</text>
          <line x1="720" y1="430" x2="720" y2="340" className="ca-edge" markerEnd="url(#ca-arrow)" />
          <text x="728" y="410" className="ca-label">stake METH</text>

          {/* MT → OG voting power (curved back-edge on the left flank).
               Endpoint now lands at OG's new left edge (x=260). */}
          <path
            d="M 60 280 L 25 280 L 25 50 L 260 50"
            className="ca-edge ca-dotted ca-edge-voting"
            markerEnd="url(#ca-arrow)"
            fill="none"
          />
          <text x="34" y="170" className="ca-label ca-label-voting">voting power · balanceOfAt</text>

          {/* ── L4 ↔ L5 — Phase 3 settlement loop (all dotted, all planned).
               Vertical arrows in the 80 px gap (y=480 → 560) plus one
               horizontal arrow across the 20 px gap between Market/Pharma
               and IP at L5 row. ─────────────────────────────────────── */}

          {/* Labs → IP (research output, downward) */}
          <line x1="450" y1="480" x2="450" y2="560" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="458" y="525" className="ca-label">research</text>

          {/* Contributors → Market/Pharma (METH sale, downward) */}
          <line x1="240" y1="480" x2="240" y2="560" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="232" y="525" className="ca-label" textAnchor="end">METH</text>

          {/* Market/Pharma → Contributors (ETH / fiat payment, upward) */}
          <line x1="260" y1="560" x2="260" y2="480" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="268" y="525" className="ca-label">ETH / fiat</text>

          {/* Market/Pharma → IP (METH for IP rights, rightward at L5 row) */}
          <line x1="340" y1="585" x2="358" y2="585" className="ca-edge ca-dotted" markerEnd="url(#ca-arrow)" />
          <text x="350" y="578" textAnchor="middle" className="ca-label">METH</text>
        </g>
      </svg>
    </div>
  );
}
